import React, { useState, useEffect } from 'react';
import './HostCardProfile.css';
import { getUserProfile } from '../utils/firestoreUser';
import PreviewProfileModal from './PreviewProfileModal';
import ReactCountryFlag from 'react-country-flag';
import { getCode as getCountryCode } from 'country-list';

// Simple Flag component (can be shared or improved)
const Flag = ({ code }) => {
    const flags = {
        FR: '🇫🇷',
        DE: '🇩🇪',
        IT: '🇮🇹',
        US: '🇺🇸'
        // Add more flags
    };
    return <span>{flags[code] || code}</span>; // Show code if flag not found
};

function HostCard({ dinner, onImageClick }) {
    const [hostProfile, setHostProfile] = useState(null);
    const [showProfilePreview, setShowProfilePreview] = useState(false);

    useEffect(() => {
        async function fetchHostProfile() {
            if (dinner.hostId) {
                const profile = await getUserProfile(dinner.hostId);
                setHostProfile(profile);
            }
        }
        fetchHostProfile();
    }, [dinner.hostId]);

    // Defensive checks
    const address = dinner.address || {};
    const dinnerDetails = dinner.dinnerDetails || {};

    // Format the dates
    const formatDates = () => {
        // Handle recurring option
        if (dinnerDetails.recurringOption) {
            const frequencyMap = {
                'weekly': 'Every week',
                'biweekly': 'Every two weeks',
                'triweekly': 'Every three weeks',
                'monthly': 'Every month'
            };
            return `${frequencyMap[dinnerDetails.recurringOption]}`;
        }

        // Handle non-recurring dates
        if (!Array.isArray(dinnerDetails.availableDates) || dinnerDetails.availableDates.length === 0) {
            return 'No dates available';
        }

        // Format each date and join them with commas
        return dinnerDetails.availableDates
            .map(date => {
                try {
                    const dateObj = new Date(date);
                    if (isNaN(dateObj.getTime())) {
                        return '';
                    }
                    return dateObj.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                } catch (error) {
                    console.error('Error formatting date:', error);
                    return '';
                }
            })
            .filter(date => date !== '') // Remove any invalid dates
            .join(', ');
    };

    // Format the location string
    const location = [
        address.neighborhood,
        address.city,
        address.country
    ].filter(Boolean).join(', ');

    // Get country code from country name
    const getCountryCodeSafe = (country) => {
        if (!country) return '';
        const code = getCountryCode(country);
        return code || '';
    };

    return (
        <div className="host-card2">
            {/* Profile Picture Section */}
            <div>
                {hostProfile?.profileImg ? (
                    <img
                        src={hostProfile.profileImg || '/blankprofile.png'}
                        alt={`${hostProfile?.name || 'Host'}'s profile`}
                        className="profile-pic2"
                        style={{ objectFit: 'cover', objectPosition: 'center', marginLeft: 8 }}
                    />
                ) : (
                    <div className="profile-pic2 host-image-placeholder2"></div>
                )}
            </div>

            {/* Content Section */}
            <div className="content-column2">
                <div className="host-info2">
                    <h3 className="host-name2">
                        {hostProfile?.name}
                    </h3>
                    <div className="location2">
                        <span>{location}</span>
                        {address.country && (
                            <ReactCountryFlag
                                countryCode={getCountryCodeSafe(address.country)}
                                svg
                                style={{ fontSize: '1em', marginLeft: 6, verticalAlign: 'middle' }}
                            />
                        )}
                    </div>
                </div>

                <div className="details2">
                    <div className="host-details-grid2">
                        <div className="detail-label"><strong>We speak:</strong></div>
                        <div className="detail-value">{hostProfile?.languages || ''}</div>
                        <div className="detail-label"><strong>Kosher Level:</strong></div>
                        <div className="detail-value">{dinnerDetails.kosherType || ''}</div>
                        <div className="detail-label"><strong>Max Guests:</strong></div>
                        <div className="detail-value">{dinnerDetails.maxGuests || ''}</div>
                        <div className="detail-label"><strong>Dinner Time:</strong></div>
                        <div className="detail-value">{dinnerDetails.time || ''}</div>
                    </div>

                    <div className="details-list2">
                        {(Array.isArray(dinnerDetails.mealStyle) ? dinnerDetails.mealStyle : []).map(style => (
                            <div key={style} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ff6b35', flexShrink: 0 }}></div>
                                <span className="meal-style-item2" style={{ background: 'transparent', padding: '0', color: '#000' }}>{style}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {dinner.shabbotAnswers?.funFact && hostProfile?.name && (
                    <div className="fun-fact-host-card2">
                        <span>
                            <strong>{hostProfile.name}'s fun fact:</strong>
                            <span style={{ fontStyle: 'italic', marginLeft: '5px' }}>
                                "{dinner.shabbotAnswers.funFact}"
                            </span>
                        </span>
                    </div>
                )}

                <div className="dates2" style={{ marginTop: '8px' }}>
                    <strong>Dates:</strong> {formatDates()}
                </div>
            </div>

            {/* This logic is now managed by Home.jsx, but can be kept for other uses if needed */}
            {showProfilePreview && hostProfile && (
                <PreviewProfileModal
                    open={showProfilePreview}
                    onClose={() => setShowProfilePreview(false)}
                    profile={{
                        uid: dinner.hostId, // Pass uid
                        avatar: hostProfile.profileImg,
                        name: hostProfile.name,
                        city: hostProfile.location?.city,
                        country: hostProfile.location?.country,
                        code: getCountryCodeSafe(hostProfile.location?.country),
                        bio: hostProfile.about,
                        languages: hostProfile.languages,
                        interests: hostProfile.interests,
                        gallery: hostProfile.gallery
                    }}
                />
            )}
        </div>
    );
}

export default HostCard;